/**
 * Service Part status rules (ISS-506): which parts count toward a ticket's totals, which a
 * manager may "Remove from ticket", and how the removal is written down.
 *
 * ONE copy, used by the server (functions: invoice draft, FSR pre-fill, the remove callable)
 * and the client (Item List, ticket/equipment parts totals, invoice review). The server is
 * the authority; the client uses the same functions to decide what to show.
 *
 * THE ENUM IS AppSheet's, VERBATIM. Ground truth, measured 2026-10-04:
 *   - app definition (docs/appsheet/appdef/archive/OPGService-942fcf5b-appdef-2025-12-03.json),
 *     `Service Parts_Schema` > `Status`: Enum, AllowOtherValues=false, the ten values below.
 *   - live mirror `appsheet_service_parts`, all 10,917 docs: exactly these ten values and no
 *     others, including 2 real rows at "6 Cancelled".
 * Never guess or re-spell a value here; AppSheet rejects anything off the list.
 */

/** Every AppSheet Service Parts Status value, in AppSheet's own order. */
export const SERVICE_PART_STATUSES: readonly string[] = Object.freeze([
  '0 Pending',
  '1 To Order',
  '2 Ordered',
  '3 Received',
  '3 In Stock',
  '4 Ready',
  '5 Installed',
  '5 Non-Part',
  '6 Returned',
  '6 Cancelled',
]);

/** The status a part removed from its ticket carries. Nothing is ever hard-deleted once ordered. */
export const SERVICE_PART_CANCELLED_STATUS = '6 Cancelled';

/** An installed part is real work on the unit; removing it needs a different flow. */
export const SERVICE_PART_INSTALLED_STATUS = '5 Installed';

/**
 * WHITELIST of statuses that count toward a ticket's parts total and its invoice. Blank is a
 * just-created row before AppSheet's initial value lands. Every AppSheet status except
 * Cancelled. An unknown status does NOT count (whitelist, not blacklist), so a new AppSheet
 * status can never leak into a total unreviewed.
 *
 * "6 Returned" still COUNTS: it is still on the ticket's list (the tech briefing shows it, a
 * bring-back the tech must see). It does NOT bill — see BILLABLE_SERVICE_PART_STATUSES. Use this
 * set for "which parts are on the ticket"; never for a money total.
 */
export const COUNTED_SERVICE_PART_STATUSES: readonly string[] = Object.freeze([
  '',
  ...SERVICE_PART_STATUSES.filter((s) => s !== SERVICE_PART_CANCELLED_STATUS),
]);

/** A part brought back / returned to the vendor. Listed, never billed (Nick, 2026-10-04). */
export const SERVICE_PART_RETURNED_STATUS = '6 Returned';

/**
 * WHITELIST of statuses that BILL: the counted set minus "6 Returned". Nick's ruling, typed in the
 * MANUAL lane 2026-10-04 19:44:48Z: "Returned parts: stop billing." Every money total a customer or
 * an invoice sees (invoice draft, invoice verification, Review & Send / Invoice Detail, the ticket
 * and equipment parts totals) uses this set. Same normalisation as the counted rule (trim; blank,
 * null and undefined read as a just-created row; anything else unknown never bills).
 */
export const BILLABLE_SERVICE_PART_STATUSES: readonly string[] = Object.freeze(
  COUNTED_SERVICE_PART_STATUSES.filter((s) => s !== SERVICE_PART_RETURNED_STATUS),
);

/**
 * Statuses a manager may "Remove from ticket" from: ordered onward, except Installed. Not-yet-
 * ordered parts (blank, Pending, To Order) keep the existing hard-delete path; Cancelled is
 * already removed.
 */
export const REMOVABLE_SERVICE_PART_STATUSES: readonly string[] = Object.freeze([
  '2 Ordered',
  '3 Received',
  '3 In Stock',
  '4 Ready',
  '5 Non-Part',
  '6 Returned',
]);

/** Roles that may remove a part from a ticket (the server re-checks from its own records). */
export const SERVICE_PART_REMOVER_ROLES: readonly string[] = Object.freeze(['Owner', 'Admin', 'Manager']);

/** A removal reason is required, and bounded. */
export const REMOVE_REASON_MIN_LENGTH = 3;
export const REMOVE_REASON_MAX_LENGTH = 500;

/** A status as stored: a string trimmed, a missing value as blank, anything else as itself. */
function normalizeStatus(status: unknown): unknown {
  if (status === undefined || status === null) return '';
  return typeof status === 'string' ? status.trim() : status;
}

/** True when a part with this status counts toward the ticket's parts total and invoice. */
export function isCountedServicePartStatus(status: unknown): boolean {
  const s = normalizeStatus(status);
  return typeof s === 'string' && COUNTED_SERVICE_PART_STATUSES.includes(s);
}

/**
 * True when a part with this status is BILLED: on invoices and in every money total. Excludes
 * "6 Cancelled" and "6 Returned". Use isCountedServicePartStatus for "is it on the ticket's list".
 */
export function isBillableServicePartStatus(status: unknown): boolean {
  const s = normalizeStatus(status);
  return typeof s === 'string' && BILLABLE_SERVICE_PART_STATUSES.includes(s);
}

/** True when the part has been removed from its ticket (Cancelled). */
export function isRemovedServicePartStatus(status: unknown): boolean {
  return normalizeStatus(status) === SERVICE_PART_CANCELLED_STATUS;
}

/** True when "Remove from ticket" applies to a part at this status. */
export function isRemovableServicePartStatus(status: unknown): boolean {
  const s = normalizeStatus(status);
  return typeof s === 'string' && REMOVABLE_SERVICE_PART_STATUSES.includes(s);
}

/** True when a user with `role` may remove a part from a ticket. Exact, case-sensitive. */
export function canRemoveServicePartsRole(role: unknown): boolean {
  return typeof role === 'string' && SERVICE_PART_REMOVER_ROLES.includes(role);
}

/**
 * The reason as it will be stored: control characters and line breaks become spaces, runs of
 * whitespace collapse, ends are trimmed. Returns null for a missing, non-string, too-short or
 * over-cap reason. Over-cap is refused, never silently truncated: the manager should see what
 * gets written.
 */
export function normalizeRemoveReason(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  // eslint-disable-next-line no-control-regex
  const cleaned = raw.replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (cleaned.length < REMOVE_REASON_MIN_LENGTH || cleaned.length > REMOVE_REASON_MAX_LENGTH) return null;
  return cleaned;
}

export interface RemovalNoteInput {
  /** Server time of the removal, ISO 8601. */
  atIso: string;
  /** The remover's login email, from the auth token. */
  byEmail: string;
  /** Already normalized by normalizeRemoveReason. */
  reason: string;
}

/** The line appended to the part's Parts Order Notes: date, who, and why. */
export function buildRemovalNote({ atIso, byEmail, reason }: RemovalNoteInput): string {
  return `[Removed from ticket ${atIso.slice(0, 10)} by ${byEmail}] ${reason}`;
}

/** Existing notes plus the removal line on a new line. Never replaces what was there. */
export function appendRemovalNote(existing: unknown, note: string): string {
  const before = typeof existing === 'string' ? existing.trimEnd() : '';
  return before.trim() ? `${before}\n${note}` : note;
}
